const BASE = '/api';

const getToken = () => localStorage.getItem('bb_token');

export const api = async (path, options = {}) => {
  const token = getToken();
  const res = await fetch(BASE + path, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: 'Bearer ' + token } : {}),
      ...options.headers,
    },
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });
  const text = await res.text();
  let data;
  try { data = text ? JSON.parse(text) : null; } catch (_) { data = null; }
  if (!res.ok) {
    let message = typeof data?.error === 'string' ? data.error
      : res.status === 429 ? 'Too many requests. Please wait before trying again.'
      : res.status === 401 ? 'Your session has expired. Please sign in again.'
      : res.status >= 500 ? 'The server could not complete this request. Please try again shortly.'
      : 'Request failed (HTTP ' + res.status + ').';
    const retryAfter = Number(res.headers.get('Retry-After'));
    if (res.status === 429 && Number.isFinite(retryAfter) && retryAfter > 0) {
      message += ' Try again in ' + Math.ceil(retryAfter / 60) + ' minute(s).';
    }
    if (res.status === 401 && path !== '/auth/login') {
      localStorage.removeItem('bb_token');
      localStorage.removeItem('bb_user');
      window.location.href = '/login';
    }
    const error = new Error(message);
    error.status = res.status;
    error.code = data?.code;
    error.details = data;
    throw error;
  }
  if (data === null && res.status !== 204) {
    throw new Error('The server returned an unexpected response. Please refresh and try again.');
  }
  return data;
};

export const getProtectedBlob = async path => {
  const token = getToken();

  const response = await fetch(BASE + path, {
    method: 'GET',
    headers: token
      ? {
          Authorization: `Bearer ${token}`
        }
      : {}
  });

  if (response.status === 401) {
    localStorage.removeItem('bb_token');
    window.location.href = '/login';

    throw new Error(
      'Your session has expired. Please sign in again.'
    );
  }

  if (!response.ok) {
    let message =
      'The protected document could not be loaded.';

    try {
      const data = await response.json();

      if (data?.error) {
        message = data.error;
      }
    } catch (error) {
      // The server may return a non-JSON error response.
    }

    throw new Error(message);
  }

  const contentType =
    response.headers.get('content-type') || '';

  if (
    !contentType
      .toLowerCase()
      .includes('application/pdf')
  ) {
    throw new Error(
      'The server did not return a valid PDF document.'
    );
  }

  return response.blob();
};

export const openProtectedFile = async path => {
  const token = getToken();

  // Opening the window before the network request avoids
  // browsers treating the eventual preview as a popup.
  const previewWindow = window.open('', '_blank');

  if (previewWindow) {
    previewWindow.opener = null;
    previewWindow.document.title = 'Loading document...';
    previewWindow.document.body.textContent =
      'Loading protected document...';
  }

  try {
    const res = await fetch(BASE + path, {
      method: 'GET',
      headers: {
        ...(token
          ? {
              Authorization:
                'Bearer ' + token
            }
          : {})
      },
      cache: 'no-store'
    });

    if (res.status === 401) {
      if (previewWindow) previewWindow.close();

      localStorage.removeItem('bb_token');
      localStorage.removeItem('bb_user');
      window.location.href = '/login';

      throw new Error(
        'Your session has expired. Please sign in again.'
      );
    }

    if (!res.ok) {
      let message =
        'The document could not be opened.';

      try {
        const errorData = await res.json();
        message = errorData.error || message;
      } catch (error) {}

      throw new Error(message);
    }

    const blob = await res.blob();
    const objectUrl =
      window.URL.createObjectURL(blob);

    if (previewWindow) {
      previewWindow.location.replace(objectUrl);
    } else {
      const link =
        document.createElement('a');

      link.href = objectUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
    }

    window.setTimeout(
      () =>
        window.URL.revokeObjectURL(
          objectUrl
        ),
      60000
    );
  } catch (error) {
    if (
      previewWindow &&
      !previewWindow.closed
    ) {
      previewWindow.close();
    }

    throw error;
  }
};

export const get  = (path) => api(path, { method: 'GET' });
export const post = (path, body) => api(path, { method: 'POST', body });
export const put  = (path, body) => api(path, { method: 'PUT', body });
export const del  = (path, body) => api(path, { method: 'DELETE', body });
